import Link from "next/link";
import { Button } from "@/components/ui/button";
import { renderChildSlot, type ChildSlot } from "@/lib/componentRegistry";
import { cn, renderRichText } from "@/lib/utils";
import { type ColorScheme, COLOR_SCHEMES } from "@/lib/colorSchemes";
import { IconList, type IconListProps } from "@/components/renderings/IconList";
import {
  type HeadingSize,
  HEADING_SIZE_CLASSES,
  type SectionPadding,
  type ContentAlign,
  contentAlignClasses,
  normalizeContentAlign,
} from "@/lib/shared-types";

/**
 * Column-split layout for the left (text) and right (slot) columns.
 * On mobile everything stacks regardless of this value.
 */
export type PageSectionLayout = "full" | "2/10" | "3/9" | "4/8" | "5/7" | "6/6";

const LAYOUT_CLASSES: Record<
  PageSectionLayout,
  { left: string; right: string; grid: string }
> = {
  full: { grid: "", left: "w-full", right: "w-full" },
  "2/10": {
    grid: "md:grid md:grid-cols-12 md:gap-8 md:items-center",
    left: "md:col-span-2",
    right: "md:col-span-10",
  },
  "3/9": {
    grid: "md:grid md:grid-cols-12 md:gap-8 md:items-center",
    left: "md:col-span-3",
    right: "md:col-span-9",
  },
  "4/8": {
    grid: "md:grid md:grid-cols-12 md:gap-8 md:items-center",
    left: "md:col-span-4",
    right: "md:col-span-8",
  },
  "5/7": {
    grid: "md:grid md:grid-cols-12 md:gap-8 md:items-center",
    left: "md:col-span-5",
    right: "md:col-span-7",
  },
  "6/6": {
    grid: "md:grid md:grid-cols-12 md:gap-8 md:items-center",
    left: "md:col-span-6",
    right: "md:col-span-6",
  },
};

export type PageSectionPadding = SectionPadding;

/**
 * Heading size preset for PageSection.
 * Re-exported from shared-types for backwards compatibility.
 */
export type PageSectionHeadingSize = HeadingSize;

/**
 * Heading alignment — re-exported from shared ContentAlign.
 * Legacy values "left" and "center" are coerced at runtime via
 * normalizeContentAlign() for backwards compatibility with existing JSON.
 */
export type PageSectionHeadingAlign = ContentAlign;

const HEADING_ALIGN_CLASSES = contentAlignClasses("md");

const PADDING_CLASSES: Record<PageSectionPadding, string> = {
  default: "py-4",
  large: "py-8",
  xlarge: "py-16",
};

export type FrameDecorationStyle = "none" | "swoop-high-right";
export type FrameDecorationColor = "white" | "neutral-cool-light" | "mint";

export interface FrameDecorationConfig {
  style: FrameDecorationStyle;
  topColor: FrameDecorationColor;
  bottomColor: FrameDecorationColor;
}

export const FRAME_DECO_HEX: Record<FrameDecorationColor, string> = {
  white: "var(--color-white)",
  "neutral-cool-light": "var(--color-neutral-cool-light)",
  mint: "var(--color-accent-mint-tint)",
};

export interface PageSectionProps {
  title: string;
  /** Optional description copy rendered below the title. */
  description?: string;
  /** Optional CTA button rendered below the description. */
  cta?: { label: string; href: string };
  /** Color scheme — controls background and text colours. Defaults to "default". */
  colorScheme?: ColorScheme;
  /** Vertical padding size. Defaults to "default" (1.6rem). */
  padding?: PageSectionPadding;
  /**
   * Heading alignment per breakpoint — "{mobile}-{desktop}".
   * Defaults to "left-left".
   */
  headingAlign?: PageSectionHeadingAlign;
  /**
   * Column layout on desktop. Defaults to "full" (no split — title/desc
   * sit above the slot). Use "2/10" through "6/6" to place
   * the text column beside the slot column.
   */
  layout?: PageSectionLayout;
  /** When true, swaps the text and slot columns (slot on left, text on right). */
  reversed?: boolean;
  /**
   * When set to "boxed", wraps the slot in a rounded card container
   * (white background, border, shadow) — matches the form card design.
   */
  slotVariant?: "default" | "boxed";
  /** Optional frame decoration — adds a swooping background shape behind the section. */
  frameDecoration?: FrameDecorationConfig;
  /** The child rendering and its props — discriminated by `type`. */
  slot: ChildSlot;
  /** Optional icon list rendered in the text column below the CTA. */
  textIconList?: IconListProps;
  /** Heading size preset. Defaults to "lg" (40 px desktop, 32 px mobile). */
  headingSize?: PageSectionHeadingSize;
  /** Optional anchor id for in-page navigation (rendered as id on the <section>). */
  anchorId?: string;
  className?: string;
}

export function PageSection({
  title,
  description,
  cta,
  colorScheme = "default",
  padding = "default",
  headingAlign: headingAlignProp = "left-left",
  layout = "full",
  reversed = false,
  slotVariant = "default",
  frameDecoration,
  slot,
  headingSize = "lg",
  textIconList,
  anchorId,
  className,
}: PageSectionProps) {
  const headingSizeClass =
    HEADING_SIZE_CLASSES[headingSize] ?? HEADING_SIZE_CLASSES["lg"];
  const lc = LAYOUT_CLASSES[layout] ?? LAYOUT_CLASSES["full"];
  const isSplit = layout !== "full" && layout in LAYOUT_CLASSES;
  const scheme = COLOR_SCHEMES[colorScheme];
  const noRightPad = slot.type === "cardCarousel";
  const isBoxed = slotVariant === "boxed";
  const hasFrameDeco = !!frameDecoration && frameDecoration.style !== "none";
  // When boxed, the section itself gets a neutral default bg;
  // the colorScheme only applies inside the inner card.
  const sectionBg = isBoxed ? COLOR_SCHEMES["default"].bg : scheme.bg;
  // Boxed form sections keep text left-aligned even on mobile.
  const forceLeft = isBoxed && slot.type === "contactForm";
  const headingAlignKey = normalizeContentAlign(
    headingAlignProp as ContentAlign | "left" | "center",
  );
  const headingAlign = forceLeft ? "" : HEADING_ALIGN_CLASSES[headingAlignKey];
  const frameTopHex = hasFrameDeco
    ? FRAME_DECO_HEX[frameDecoration!.topColor]
    : "#ffffff";
  const frameBottomHex = hasFrameDeco
    ? FRAME_DECO_HEX[frameDecoration!.bottomColor]
    : "#e8eef2";
  const frameBg = hasFrameDeco ? (
    <>
      {/* Desktop decoration (md+) */}
      <svg
        aria-hidden
        focusable="false"
        className="absolute inset-0 w-full h-full hidden md:block"
        viewBox="0 0 1440 423"
        preserveAspectRatio="none"
        style={{ zIndex: 0 }}
      >
        <defs>
          <clipPath id="frame-clip-d">
            <rect width="1440" height="423" />
          </clipPath>
        </defs>
        <g clipPath="url(#frame-clip-d)">
          <rect width="1440" height="423" fill={frameBottomHex} />
          {frameDecoration!.style === "swoop-high-right" && (
            /* Large circle arc — centre (290.88, −3285.99), r 3500.
               Derived from Figma matrix(-1 0 0 1 3790.88 -6785.99). */
            <circle cx="290.88" cy="-3285.99" r="3500" fill={frameTopHex} />
          )}
        </g>
      </svg>
      {/* Mobile decoration (<md) */}
      <svg
        aria-hidden
        focusable="false"
        className="absolute inset-0 w-full h-full md:hidden"
        viewBox="0 0 400 690"
        preserveAspectRatio="none"
        style={{ zIndex: 0 }}
      >
        <defs>
          <clipPath id="frame-clip-m">
            <rect width="400" height="690" />
          </clipPath>
        </defs>
        <g clipPath="url(#frame-clip-m)">
          <rect width="400" height="690" fill={frameBottomHex} />
          {frameDecoration!.style === "swoop-high-right" && (
            /* Smaller circle for narrow viewport — centre (48.336, −565.95), r 750.
               Derived from Figma matrix(-1 0 0 1 798.336 -1315.95). */
            <circle cx="48.336" cy="-565.95" r="750" fill={frameTopHex} />
          )}
        </g>
      </svg>
    </>
  ) : null;

  if (isSplit) {
    const textCol = (
      <div
        className={cn(
          reversed ? lc.right : lc.left,
          noRightPad && "pr-4 md:pr-0",
          // On mobile the grid collapses and columns stack — add bottom
          // margin so the text column separates from the slot below.
          "mb-6 md:mb-0",
          // When reversed, the slot stacks above text — use top margin instead.
          reversed && "mt-8 md:mt-0",
        )}
      >
        <h2
          className={cn(
            `${headingSizeClass} ${headingAlign} font-semibold mb-4`,
            scheme.heading,
          )}
        >
          {title}
        </h2>
        {description && (
          <p className={cn("text-body-md mb-4", scheme.bodyMuted)}>
            {renderRichText(description)}
          </p>
        )}
        {cta && (
          <Button
            className="mt-4"
            nativeButton={false}
            render={
              cta.href.startsWith("#") ? (
                <a href={cta.href} />
              ) : (
                <Link href={cta.href} />
              )
            }
          >
            {cta.label}
          </Button>
        )}
        {textIconList && textIconList.items?.length > 0 && (
          <div className="mt-4 md:mt-8">
            <IconList
              {...textIconList}
              colorScheme={textIconList.colorScheme ?? colorScheme}
            />
          </div>
        )}
      </div>
    );

    const slotCol = (
      <div className={cn("min-w-0", reversed ? lc.left : lc.right)}>
        {renderChildSlot(slot, colorScheme)}
      </div>
    );

    const inner = (
      <div className={cn(lc.grid)}>
        {reversed ? (
          <>
            {slotCol}
            {textCol}
          </>
        ) : (
          <>
            {textCol}
            {slotCol}
          </>
        )}
      </div>
    );

    return (
      <section
        id={anchorId}
        className={cn(
          className,
          noRightPad ? "pl-6 overflow-x-clip" : "px-6",
          PADDING_CLASSES[padding],
          hasFrameDeco ? "relative overflow-hidden" : sectionBg,
        )}
      >
        {frameBg}
        <div
          className={cn(
            !noRightPad && "max-w-content mx-auto",
            hasFrameDeco && "relative z-1",
          )}
          style={
            noRightPad
              ? { paddingLeft: "max(0px, calc((100% - 128rem) / 2))" }
              : undefined
          }
        >
            {isBoxed ? (
            <div
              className={cn(
                "rounded-2xl shadow-xl overflow-hidden p-8 md:p-12",
                scheme.bg,
              )}
            >
              {inner}
            </div>
          ) : (
            inner
          )}
        </div>
      </section>
    );
  }

  // Full-width: title + description above the slot, left-aligned to frame edge
  const fullInner = (
    <>
      {(title || description) && (
        <div className={cn("text-left", noRightPad && "pr-4 md:pr-0")}>
          <h2
            className={cn(
              `${headingAlign} ${headingSizeClass} font-semibold mb-6`,
              scheme.heading,
            )}
          >
            {title}
          </h2>
          {description && (
            <p className={cn("text-body-md max-w-2xl mb-4", scheme.bodyMuted)}>
              {renderRichText(description)}
            </p>
          )}
          {cta && (
            <Button
              className="mt-4"
              nativeButton={false}
              render={
                cta.href.startsWith("#") ? (
                  <a href={cta.href} />
                ) : (
                  <Link href={cta.href} />
                )
              }
            >
              {cta.label}
            </Button>
          )}
          {textIconList && textIconList.items?.length > 0 && (
            <div className="mt-4 md:mt-8">
              <IconList
                {...textIconList}
                colorScheme={textIconList.colorScheme ?? colorScheme}
              />
            </div>
          )}
        </div>
      )}
            {renderChildSlot(slot, colorScheme)}
    </>
  );

  return (
    <section
      id={anchorId}
      className={cn(
        className,
        "pl-6",
        !noRightPad ? "pr-6" : "overflow-x-clip",
        PADDING_CLASSES[padding],
        hasFrameDeco ? "relative overflow-hidden" : sectionBg,
      )}
    >
      {frameBg}
      <div
        className={cn("max-w-content mx-auto", hasFrameDeco && "relative z-1")}
      >
        {isBoxed ? (
          <div
            className={cn(
              "rounded-2xl shadow-xl overflow-hidden p-8 md:p-12",
              scheme.bg,
            )}
          >
            {fullInner}
          </div>
        ) : (
          fullInner
        )}
      </div>
    </section>
  );
}

export default PageSection;
